import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { useDataProvider, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';
import { ContactAutocomplete } from '../../components/ContactAutocomplete.tsx';
import ContactQuickAdd from '../../components/ra-forms/ContactQuickAdd.tsx';

const AddContactToCompanyModal: React.FC = () => {
  const company = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [update, { isLoading }] = useUpdate();
  const [open, setOpen] = React.useState(false);
  const [selectedContactId, setSelectedContactId] = React.useState<number | null>(null);

  if (!company?.id) return null;

  const handleSave = async () => {
    if (!selectedContactId) {
      notify('Select a contact to add.', { type: 'warning' });
      return;
    }

    try {
      const { data: contact } = await dataProvider.getOne('contacts', {
        id: selectedContactId
      });

      update(
        'contacts',
        {
          id: selectedContactId,
          data: { ...contact, companyId: company.id }
        },
        {
          onSuccess: () => {
            notify('Contact added', { type: 'info' });
            refresh();
            setOpen(false);
            setSelectedContactId(null);
          },
          onError: (error) => {
            if (error instanceof Error) {
              notify(`Error: ${error.message}`, { type: 'error' });
            } else {
              notify('An unexpected error occurred', { type: 'error' });
            }
          }
        }
      );
    } catch (error) {
      if (error instanceof Error) {
        notify(`Error: ${error.message}`, { type: 'error' });
      } else {
        notify('An unexpected error occurred', { type: 'error' });
      }
    }
  };

  return (
    <>
      <Button variant="contained" size="small" onClick={() => setOpen(true)}>
        Add Contact
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ContactAutocomplete
              value={selectedContactId}
              onChange={(id) => setSelectedContactId(id)}
              label="Select Contact"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Not in the list?
              </Typography>
              <ContactQuickAdd
                simple={false}
                companyId={company.id as number}
                onContactCreated={() => {
                  refresh();
                  setOpen(false);
                  setSelectedContactId(null);
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddContactToCompanyModal;
